import * as React from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { Card, CardContent, Grid } from '@mui/material';

import { dispatchErrors } from '../../../../../../datahub/errorUtils';
import selectUser from '../../../../../store/selectors/appSelector';
import { BLACK } from '../../../../../theme/colorConstants';
import { showSnackbar } from '../../../../app';
import { ContractApiService } from '../../../../contracts/services';

import { transformContractData } from './helpers/contractDataTransformers';
import {
  ContractCardActions,
  ContractCardHeader,
  ContractCardRates,
  ContractCardStipend,
} from './components';
import ImpactedPositionsDisplayModal from './ImpactedPositionsDisplayModal';

const ContractCard = ({ contract, refreshContractData }) => {
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const transformedContract = transformContractData(contract);
  const contractType = contract?.rates?.type;

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleDeleteContract = async () => {
    setIsDeleting(true);

    try {
      await ContractApiService.deleteContract(
        contract._id,
        user,
        dispatch,
        () => {
          dispatch(showSnackbar({ message: 'contract.validation.deleteSuccess' }));
          // Call the callback to refresh data
          if (refreshContractData) {
            refreshContractData();
          }

          setIsDeleting(false);
        },
        (d) => {
          dispatchErrors({ dispatch, responsePayload: d.response });
          setIsDeleting(false);
        }
      );
    } catch (error) {
      dispatch(showSnackbar({ message: 'contract.validation.deleteError' }));
      setIsDeleting(false);
    }
  };

  return (
    <Grid container>
      <Grid item xs={12}>
        <Card
          sx={{
            border: `1px solid ${BLACK[40]}`,
            borderRadius: '12px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          }}
        >
          <CardContent
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              padding: '24px',
            }}
          >
            {/* Header Section */}
            <ContractCardHeader contract={contract} transformedContract={transformedContract} />

            {/* Main Information Grid */}
            <ContractCardRates
              contractType={contractType}
              transformedContract={transformedContract}
            />

            {/* Stipend Information */}
            <ContractCardStipend
              mealAmount={transformedContract?.stipend?.mealAmount}
              lodgingAmount={transformedContract?.stipend?.lodgingAmount}
              targetHours={transformedContract?.stipend?.targetTimeAccrued}
              prorateDropOff={transformedContract?.stipend?.prorateDropoffTimeAccrued}
            />

            {/* Action Buttons */}
            <ContractCardActions
              onViewPositions={handleOpenModal}
              onDeleteContract={handleDeleteContract}
              isDeleting={isDeleting}
            />
          </CardContent>
        </Card>
      </Grid>

      {/* Positions Impacted Modal */}
      <ImpactedPositionsDisplayModal
        open={modalOpen}
        onClose={handleCloseModal}
        positionTemplates={contract?.contractScope?.positionTemplates}
      />
    </Grid>
  );
};

ContractCard.propTypes = {
  contract: PropTypes.shape({
    _id: PropTypes.string,
    contractScope: PropTypes.shape({
      agency: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      corporation: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
      positionTemplates: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        })
      ),
      worker: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
    state: PropTypes.shape({
      status: PropTypes.string,
    }),
    timeWindow: PropTypes.shape({
      start: PropTypes.number,
      end: PropTypes.number,
    }),
    metadata: PropTypes.shape({
      created: PropTypes.string,
      modified: PropTypes.string,
      creator: PropTypes.string,
    }),
    rates: PropTypes.shape({
      type: PropTypes.string,
      fixedRates: PropTypes.shape({
        pay: PropTypes.shape({
          reg: PropTypes.number,
          OT: PropTypes.number,
        }),
        bill: PropTypes.shape({
          reg: PropTypes.number,
          OT: PropTypes.number,
        }),
      }),
      marginRates: PropTypes.shape({
        reg: PropTypes.number,
        OT: PropTypes.number,
      }),
    }),
  }),
  refreshContractData: PropTypes.func,
};

export default ContractCard;
